import React from 'react';
import {Box, Button} from '@mui/material';
import classNames from 'classnames';
import {FormattedMessage} from 'react-intl';
import {SVGS} from '../../../assets/images';
import {Lx} from '../../../i18n/consts';
import {FilterPopover, FilterPreviewPopover} from '../filter-popover';
import styles from './property-filter-popup.module.scss';

interface Props {
    selectedFiltersCount: number;
    previewContent: React.ReactNode;
    onApply: () => void;
    onReset: () => void;
    onCancel: () => void;
    children: React.ReactNode;
    disabled?: boolean;
    compactNarrowDesktop?: boolean;
}

export default function PropertyFilterPopup(props: Props) {
    const {
        selectedFiltersCount,
        previewContent,
        onApply,
        onReset,
        onCancel,
        children,
    } = props;

    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const [readOnlyAnchorEl, setReadOnlyAnchorEl] = React.useState<HTMLElement | null>(null);

    const open = Boolean(anchorEl);
    const id = open ? 'filter-change-popover' : undefined;

    const readOnlyOpen = Boolean(readOnlyAnchorEl);
    const readOnlyId = readOnlyOpen ? 'filter-view-popover' : undefined;

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (props.disabled) {
            return;
        }
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        onCancel();
        setAnchorEl(null);
    };

    const handleApply = () => {
        onApply();
        setAnchorEl(null);
    };

    const handleReadOnlyPopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
        setReadOnlyAnchorEl(event.currentTarget);
    };

    const handleReadOnlyPopoverClose = () => {
        setReadOnlyAnchorEl(null);
    };

    return (
        <>
            <Button
                onClick={handleClick}
                className={classNames(styles.filterButton, {
                    [styles.focused]: open,
                    [styles.active]: selectedFiltersCount > 0,
                    [styles.compactNarrowDesktop]: props.compactNarrowDesktop
                })}
                disabled={props.disabled}
            >
                <SVGS.FilterIcon />
                <span className={styles.filterLabel}>
                    <FormattedMessage id={Lx.General.FILTER} />
                </span>
                {selectedFiltersCount > 0 && (
                    <Box
                        className={styles.filterCount}
                        onMouseEnter={handleReadOnlyPopoverOpen}
                        onMouseLeave={handleReadOnlyPopoverClose}
                    >
                        {selectedFiltersCount}
                    </Box>
                )}
            </Button>
            <FilterPreviewPopover
                id={readOnlyId}
                open={readOnlyOpen}
                anchorEl={readOnlyAnchorEl}
                onClose={handleReadOnlyPopoverClose}
            >
                {previewContent}
            </FilterPreviewPopover>
            <FilterPopover id={id} open={open} anchorEl={anchorEl}>
                <Box className={styles.filterHeader}>
                    <FormattedMessage id={Lx.General.FILTER} />
                    <Button onClick={handleClose} disabled={props.disabled}><SVGS.CancelIcon /></Button>
                </Box>
                <Box className={styles.filtersWrapper}>
                    {children}
                </Box>
                <Box className={styles.filterFooter}>
                    <Button onClick={onReset} className={classNames(styles.filterFooterButton, styles.clearButton)} disabled={props.disabled}>
                        <FormattedMessage id={Lx.General.CLEAR_FILTERS} />
                    </Button>
                    <Button onClick={handleApply} className={classNames(styles.filterFooterButton, styles.applyButton)} disabled={props.disabled}>
                        <FormattedMessage id={Lx.General.APPLY} />
                    </Button>
                </Box>
            </FilterPopover>
        </>
    );
}
